import { Shape } from '@/services/shape';
import {
  addResourceIfNotExists,
  generateLayoutAst,
  insertImages
} from '../utils/resource.utils';
import { SpriteAttribute } from '@/services/shape/attributes/sprite';
import { getRefName } from '../utils/name.utils';
import { buildAstState } from '..';

export const getVideoSpriteAst = (resourceAst: Ast, shape: Shape) => {
  const {
    repeatCount = 0,
    infinite = false,
    videoSrc = '',
    resources,
    format,
    videoTypeToShow = 'video',
    videoColorData
  } = shape.attrs as SpriteAttribute;

  const deviceType = buildAstState.data.description?.deviceType;
  const isAndroidDevice = buildAstState.data.isAndroidDevice;
  const supportsAdvanced =
    buildAstState.data.description?.watchOS == 'android' && isAndroidDevice;

  // 获取当前主题的第一个配色 id，用于读取该样式下的视频数据
  const currentColorGroup =
    buildAstState.data.theme?.colorGroupTable?.[0]?.id ?? '';

  // 在使用高级功能，即watchOS == 'android'时，
  // 在智能表下和非智能表需要区分处理

  const spriteAst: Ast = {
    tag: 'Sprite',
    attrsMap: {
      repeatCount: infinite ? '0' : repeatCount.toString()
    },
    children: []
  };

  const spriteItems = shape.getSpriteMulitItems(true, currentColorGroup) ?? [];

  /**
   * 插入视频文件ast，并返回视频名称
   * @returns
   */
  const _insertVideoFileAst = (_videoSrc: string, previewRef?: string) => {
    const fileName = String(_videoSrc ?? '');
    let videoFileAst: Ast = {
      tag: 'File',
      attrsMap: {
        name: fileName,
        fileName
      },
      children: []
    };

    // 为app单独添加预览图
    if (buildAstState.options.exportForApp && previewRef) {
      videoFileAst.attrsMap.preview = previewRef;
    }

    videoFileAst = addResourceIfNotExists(
      resourceAst,
      videoFileAst,
      undefined,
      true
    );

    const videoName = getRefName(videoFileAst.attrsMap.name);
    return videoName;
  };

  // 如果是多序列帧多视频，需要增加SpriteInfo
  if (buildAstState.data.isMultiSprite) {
    const spriteGroupAst: Ast = {
      tag: 'SpriteGroup',
      attrsMap: {
        repeatCount: infinite ? '0' : repeatCount.toString(),
        count: spriteItems.length.toString()
      },
      children: []
    };

    // 如果是大核设备，需要将预览图插入到resource中
    const imagesMap =
      isAndroidDevice || deviceType !== 'O61'
        ? insertImages(resourceAst, resources, {
            recolorEnable: false,
            imgFormat: format
          })
        : {};

    spriteGroupAst.children = spriteItems.map((spriteItem, index) => {
      const src = spriteItem.videoSrc;
      // 预览图
      const videoPreviewName = spriteItem.videoPreviewName ?? 'videoPreview';
      const previewRef = getRefName(imagesMap[videoPreviewName]);

      const videoName =
        src && (deviceType !== 'O61' || supportsAdvanced)
          ? _insertVideoFileAst(src, previewRef)
          : '';
      const param: Ast = {
        tag: 'SpriteInfo',
        attrsMap: {
          // 大核时：ref=预览图，refExt=视频文件
          // 非大核：ref=视频文件
          // 编辑器为editor状态需要videoName来预览视频
          ref:
            isAndroidDevice &&
            previewRef &&
            buildAstState.options.buildFor !== 'editor'
              ? previewRef
              : videoName,
          // video_index: index.toString() // 固件去掉video_index
          // 新增视频的默认展示default_index，但还暂未确定功能，暂时不设置
          default_index: '0'
        },
        children: []
      };
      // 如果是大核且支持高级功能，即watchOS == 'android'时
      if (supportsAdvanced) {
        param.attrsMap.refExt = videoName;
      }
      return param;
    });

    // 如果是本地则将需要展示的视频设置在sprite标签上
    if (buildAstState.options.buildFor == 'editor') {
      spriteGroupAst.attrsMap._name = 'spriteGroup工具显示用';
      spriteGroupAst.attrsMap.ref =
        spriteGroupAst.children?.[0]?.attrsMap?.ref ?? '';
      spriteGroupAst.tag = 'Sprite';
    }
    return spriteGroupAst;
  } else {
    // 如果是大核设备，需要将预览图插入到resource中
    // 需求更改：需要增加预览图，不需要判断是否大核
    let imagesMap;
    let previewRef;
    if (resources && resources['videoPreview']) {
      imagesMap = insertImages(
        resourceAst,
        { videoPreview: resources['videoPreview'] },
        {
          recolorEnable: false,
          imgFormat: format
        }
      );
      previewRef = getRefName(imagesMap['videoPreview']);
    }

    // 直接将视频file添加即可
    // 优先使用当前样式的视频数据，fallback 到任意已有样式
    const resolvedVideoSrc =
      (currentColorGroup && videoColorData?.[currentColorGroup]?.videoSrc) ||
      (videoColorData
        ? Object.values(videoColorData).find((v) => !!v?.videoSrc)?.videoSrc
        : undefined) ||
      videoSrc;

    const videoName =
      deviceType !== 'O61' || supportsAdvanced
        ? _insertVideoFileAst(resolvedVideoSrc, previewRef)
        : '';
    spriteAst.attrsMap.ref =
      isAndroidDevice && previewRef ? previewRef : videoName;

    if (supportsAdvanced) {
      spriteAst.attrsMap.refExt = videoName;
    }
    return spriteAst;
  }
};

const getImagesSpriteAst = (resourceAst: Ast, shape: Shape) => {
  const {
    resources,
    repeatCount = 0,
    infinite = false,
    spriteFps = 60,
    format,
    spriteDefaultIndex = 0
  } = shape.attrs as SpriteAttribute;

  // 如果是多序列帧多视频，需要增加SpriteInfo
  if (buildAstState.data.isMultiSprite) {
    const spriteItems = shape.getSpriteMulitItems(true) ?? [];

    const spriteGroupAst: Ast = {
      tag: 'SpriteGroup',
      attrsMap: {
        // ref: getRefName(imageMap['images']),
        repeatCount: infinite ? '0' : repeatCount.toString(),
        interval: String(Math.floor(1000 / spriteFps)),
        count: spriteItems.length.toString()
      },
      children: []
    };

    // 添加图片
    const imageMap = insertImages(resourceAst, resources, {
      // 序列帧图片不支持重着色
      recolorEnable: false,
      imgFormat: format
    });

    spriteGroupAst.children = spriteItems.map((spriteItem) => {
      const resourceName = spriteItem.spriteImagesName as 'images';
      const ref = getRefName(imageMap[resourceName]);
      return {
        tag: 'SpriteInfo',
        attrsMap: {
          ref,
          default_index: String(spriteItem.spriteDefaultIndex ?? 0)
        },
        children: []
      };
    });

    // 如果是本地则将需要展示的视频设置在sprite标签上
    if (buildAstState.options.buildFor == 'editor') {
      const firstItem = spriteGroupAst.children?.[0];
      spriteGroupAst.attrsMap = {
        ...spriteGroupAst.attrsMap,
        ref: firstItem?.attrsMap?.ref ?? '',
        _defaultIndex: firstItem.attrsMap.default_index ?? '0',
        _name: 'spriteGroup工具显示用'
      };
      spriteGroupAst.tag = 'Sprite';
    }
    return spriteGroupAst;
  } else {
    const spriteAst: Ast = {
      tag: 'Sprite',
      attrsMap: {
        // ref: getRefName(imageMap['images']),
        repeatCount: infinite ? '0' : repeatCount.toString(),
        interval: String(Math.floor(1000 / spriteFps))
      },
      children: []
    };
    // 添加图片
    const imageMap = insertImages(resourceAst, resources, {
      // 序列帧图片不支持重着色
      recolorEnable: false,
      imgFormat: format
    });
    spriteAst.attrsMap.ref = getRefName(imageMap['images']);

    // 添加默认显示，使用_前缀
    spriteAst.attrsMap._defaultIndex = String(spriteDefaultIndex);
    return spriteAst;
  }
};

export const getSpriteAst = (resourceAst: Ast, shape: Shape) => {
  const {
    x = 0,
    y = 0,
    spriteType = 'sprite'
  } = shape.attrs as SpriteAttribute;

  const spriteAst =
    spriteType == 'sprite'
      ? getImagesSpriteAst(resourceAst, shape)
      : getVideoSpriteAst(resourceAst, shape);

  const ast = addResourceIfNotExists(resourceAst, spriteAst);

  // 生成 layout ast
  const layoutAst: Ast = generateLayoutAst({
    ref: '@' + ast.attrsMap.name,
    x,
    y
  });

  return layoutAst;
};
